/* ================================================================
   PVAnnuelPage.tsx — Procès-Verbal Annuel (CORRIGÉ V3)
   ================================================================ */

import { useState, useEffect, useCallback, useMemo } from "react";
import {
  FileText, Printer, FileSpreadsheet, Download,
  Loader2, Search, Settings, Calendar,
  Building2, Save, ChevronDown, ChevronUp, Scale
} from "lucide-react";
import { apiService } from "@/services/api";
import { toast } from "sonner";
import { useLang } from "@/hooks/useLang";

// ==================== HELPERS ROBUSTES ====================
const extractData = (res: any): any[] => {
  if (Array.isArray(res)) return res;
  if (!res || typeof res !== "object") return [];
  const findArray = (obj: any, depth: number = 0): any[] | null => {
    if (depth > 4) return null;
    if (Array.isArray(obj)) {
      if (obj.length > 0 && typeof obj[0] === "object" && obj[0] !== null && !Array.isArray(obj[0])) return obj;
      if (obj.length === 0) return obj;
      return null;
    }
    if (!obj || typeof obj !== "object") return null;
    for (const key of ["items", "data", "results", "students", "grades", "records", "users", "classes", "content"]) {
      const found = findArray(obj[key], depth + 1);
      if (found !== null) return found;
    }
    return null;
  };
  const found = findArray(res);
  if (found !== null) return found;
  return [];
};

const AUTH_STORAGE_KEY = "auth-storage";
const SELECTED_YEAR_ID_KEY = "lybibal-selected-year-id";
const SELECTED_YEAR_LABEL_KEY = "lybibal-selected-year-label";
const SELECTED_YEAR_EVENT = "lybibal-year-change";

const getUserSchoolId = (): number | null => {
  try {
    const authRaw = localStorage.getItem(AUTH_STORAGE_KEY);
    if (authRaw) {
      const parsed = JSON.parse(authRaw);
      const state = parsed?.state || parsed;
      if (state?.user?.school_id) return Number(state.user.school_id);
    }
  } catch (e) { console.warn("Failed to get school_id:", e); }
  return null;
};

const getSelectedYearId = (): number | null => {
  try {
    const yearId = localStorage.getItem(SELECTED_YEAR_ID_KEY);
    if (yearId) return Number(yearId);
  } catch (e) { console.warn("Failed to get year_id:", e); }
  return null;
};

function getValue(obj: any, ...keys: string[]): any {
  if (!obj || typeof obj !== "object") return undefined;
  for (const key of keys) {
    if (obj[key] !== undefined && obj[key] !== null) return obj[key];
    if (obj.student && typeof obj.student === "object" && !Array.isArray(obj.student)) {
      if (obj.student[key] !== undefined && obj.student[key] !== null) return obj.student[key];
    }
    if (obj.user && typeof obj.user === "object" && !Array.isArray(obj.user)) {
      if (obj.user[key] !== undefined && obj.user[key] !== null) return obj.user[key];
    }
    for (const subKey of Object.keys(obj)) {
      const sub = obj[subKey];
      if (sub && typeof sub === "object" && !Array.isArray(sub) && subKey !== "student" && subKey !== "user") {
        if (sub[key] !== undefined && sub[key] !== null) return sub[key];
      }
    }
  }
  return undefined;
}

function getStr(obj: any, ...keys: string[]): string {
  const v = getValue(obj, ...keys);
  return v !== undefined && v !== null ? String(v) : "";
}

function getNum(obj: any, ...keys: string[]): number {
  const v = getValue(obj, ...keys);
  if (v === undefined || v === null) return 0;
  const n = Number(v);
  return isNaN(n) ? 0 : n;
}

function getStudentId(r: any): string | undefined {
  if (!r || typeof r !== "object") return undefined;
  if (r.student_id !== undefined && r.student_id !== null) return String(r.student_id);
  if (r.student && typeof r.student === "object" && !Array.isArray(r.student)) {
    if (r.student.id !== undefined && r.student.id !== null) return String(r.student.id);
    if (r.student.student_id !== undefined && r.student.student_id !== null) return String(r.student.student_id);
  }
  if (r.user && typeof r.user === "object" && !Array.isArray(r.user)) {
    if (r.user.id !== undefined && r.user.id !== null) return String(r.user.id);
  }
  return undefined;
}

function getBool(obj: any, ...keys: string[]): boolean {
  const v = getValue(obj, ...keys);
  if (v === undefined || v === null) return false;
  if (typeof v === "boolean") return v;
  if (typeof v === "string") return v.toLowerCase() === "true" || v === "1" || v.toLowerCase() === "oui" || v.toLowerCase() === "yes";
  if (typeof v === "number") return v === 1;
  return false;
}

function downloadCSV(filename: string, rows: string[][]) {
  const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(";")).join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
}

function downloadExcelData(filename: string, data: any[], schoolHeader?: SchoolHeader, title?: string, subtitle?: string) {
  const rows: string[][] = [];
  if (schoolHeader) {
    rows.push([schoolHeader.school_name]);
    rows.push([schoolHeader.school_name_en]);
    rows.push([schoolHeader.address]);
    rows.push([""]);
  }
  if (title) {
    rows.push([title]);
    if (subtitle) rows.push([subtitle]);
    rows.push([""]);
  }
  const headers = Object.keys(data[0] || {});
  rows.push(headers);
  data.forEach((row) => {
    rows.push(headers.map((h) => row[h] ?? ""));
  });
  downloadCSV(filename, rows);
}

function aggregateGradesByStudent(records: any[]): Map<string, any> {
  const map = new Map<string, any>();
  for (const r of records) {
    const sid = getStudentId(r);
    if (sid === undefined || sid === null) continue;
    const sidStr = String(sid);
    if (!map.has(sidStr)) {
      map.set(sidStr, { student_id: sid, _totalPoints: 0, _totalCoeff: 0, _count: 0 });
    }
    const entry = map.get(sidStr)!;
    const score = getNum(r, "score", "value", "note", "grade_value");
    const coeff = getNum(r, "coefficient", "subject_coefficient", "coef") || 1;
    const points = getNum(r, "points");
    if (points > 0) { entry._totalPoints += points; entry._totalCoeff += coeff; }
    else if (score > 0) { entry._totalPoints += score * coeff; entry._totalCoeff += coeff; }
    entry._count += 1;
  }
  for (const [sid, entry] of map) {
    entry.moyenne = entry._totalCoeff > 0 ? Math.round((entry._totalPoints / entry._totalCoeff) * 100) / 100 : 0;
    entry.nmc = entry._count;
  }
  return map;
}

async function loadClassStudents(classId: number, params: any): Promise<any[]> {
  try {
    const res = await apiService.get("/students", { params: { ...params, class_id: classId } });
    const data = extractData(res);
    console.log(`[PVAnn] Class ${classId} students:`, data.length, data[0] ? Object.keys(data[0]) : "empty");
    return data;
  } catch (e: any) {
    console.error(`[PVAnn] Failed class ${classId} students:`, e.message || e);
    return [];
  }
}

async function loadDisciplineForClass(classId: number, params: any): Promise<Map<string, { anj: number; jex: number }>> {
  try {
    const res = await apiService.get("/discipline", { params: { ...params, class_id: classId, per_page: 1000 } });
    const items = extractData(res);
    console.log(`[PVAnn] Class ${classId} discipline:`, items.length);
    const map = new Map<string, { anj: number; jex: number }>();
    for (const r of items) {
      const sid = String(getValue(r, "student_id"));
      if (!sid || sid === "undefined" || sid === "null") continue;
      if (!map.has(sid)) map.set(sid, { anj: 0, jex: 0 });
      const entry = map.get(sid)!;
      const type = String(getStr(r, "incident_type")).toLowerCase();
      const justified = getBool(r, "justified");
      if (type === "absence" && !justified) entry.anj += getNum(r, "duration_hours");
      if (type === "exclusion" || getStr(r, "sanction").toLowerCase().includes("exclusion")) entry.jex += getNum(r, "duration_days");
    }
    return map;
  } catch (e: any) {
    console.error(`[PVAnn] Failed discipline ${classId}:`, e.message || e);
    return new Map();
  }
}

async function loadGrades(params: any): Promise<any[]> {
  try {
    const res = await apiService.get("/grades", { params });
    const data = extractData(res);
    console.log(`[PVAnn] Grades:`, data.length, "params:", JSON.stringify(params));
    return data;
  } catch (e: any) {
    console.error("[PVAnn] Failed grades:", e.message || e);
    return [];
  }
}

// ==================== TYPES ====================
interface SchoolHeader {
  republic_fr: string; republic_en: string; motto_fr: string; motto_en: string;
  region: string; department: string; delegation: string; sub_division: string;
  school_name: string; school_name_en: string; address: string; po_box: string; logo_url: string;
}

interface PVAnnuelStudent {
  id: number; matricule: string; first_name: string; last_name: string;
  gender: string; birth_date: string; class_name: string;
  trim1: number; trim2: number; trim3: number; moy_ann: number; rang: number;
  anj: number; jex: number; decision: string; conduite: string; travail: string;
  redouble: boolean; nmc: number;
}

function normalizeFromClassStudent(cs: any, t1Entry?: any, t2Entry?: any, t3Entry?: any, disc?: {anj:number;jex:number}): PVAnnuelStudent {
  const firstName = getStr(cs, "first_name", "prenom", "firstname", "firstName", "prenoms", "given_name", "name") || "—";
  const lastName = getStr(cs, "last_name", "nom", "lastname", "lastName", "noms", "family_name", "surname") || "—";
  const matricule = getStr(cs, "matricule", "registration_number", "matricule_nat", "code") || `ID${getValue(cs, "id") ?? 0}`;
  const gender = getStr(cs, "gender", "sex", "sexe", "genre") || "—";
  const birthDate = getStr(cs, "birth_date", "date_of_birth", "date_naissance", "dob", "birthdate") || "";
  const t1 = t1Entry?.moyenne ?? 0;
  const t2 = t2Entry?.moyenne ?? 0;
  const t3 = t3Entry?.moyenne ?? 0;
  const activeTerms = [t1, t2, t3].filter((x) => x > 0).length;
  const moyAnn = activeTerms > 0 ? Math.round(((t1 + t2 + t3) / activeTerms) * 100) / 100 : 0;
  let decision = "";  // Toujours vide — le proviseur remplit manuellement
  return {
    id: getValue(cs, "id", "student_id") ?? 0,
    matricule, first_name: firstName, last_name: lastName, gender, birth_date: birthDate,
    class_name: getStr(cs, "class_name", "classe") || "—",
    trim1: t1, trim2: t2, trim3: t3, moy_ann: moyAnn, rang: 0,
    anj: disc?.anj ?? 0, jex: disc?.jex ?? 0,
    decision,
    conduite: getStr(cs, "conduite", "conduct", "behavior", "student_conduct") || "B",
    travail: getStr(cs, "travail", "work", "effort", "student_work") || "B",
    redouble: getBool(cs, "redouble", "is_repeating", "repeating", "student_is_repeating"),
    nmc: (t1Entry?.nmc || 0) + (t2Entry?.nmc || 0) + (t3Entry?.nmc || 0),
  };
}

function generateMockPVAnnuel(count: number): PVAnnuelStudent[] {
  const firstNames = ["Jean","Marie","Pierre","Anne","Paul","Sophie","Michel","Catherine","Robert","Christine"];
  const lastNames = ["Kamga","Fouda","Ndjomo","Tchinda","Mbarga","Essomba","Mba","Ngono","Biya","Muna"];
  const students: PVAnnuelStudent[] = [];
  for (let i = 1; i <= count; i++) {
    const t1 = Math.round((4 + Math.random() * 16) * 10) / 10;
    const t2 = Math.round((4 + Math.random() * 16) * 10) / 10;
    const t3 = Math.round((4 + Math.random() * 16) * 10) / 10;
    const moy = Math.round(((t1 + t2 + t3) / 3) * 10) / 10;
    let dec = "";
    const year = 2005 + Math.floor(Math.random() * 8);
    const month = 1 + Math.floor(Math.random() * 12);
    const day = 1 + Math.floor(Math.random() * 28);
    students.push({
      id: i, matricule: `CM26${String(i).padStart(5, "0")}`,
      first_name: firstNames[Math.floor(Math.random() * firstNames.length)],
      last_name: lastNames[Math.floor(Math.random() * lastNames.length)],
      gender: Math.random() > 0.45 ? "M" : "F",
      birth_date: `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`,
      class_name: "6E1", trim1: t1, trim2: t2, trim3: t3, moy_ann: moy, rang: 0,
      anj: Math.floor(Math.random() * 10), jex: Math.random() > 0.9 ? Math.floor(Math.random() * 5) : 0,
      decision: dec, conduite: ["A","B","B","C","A"][Math.floor(Math.random() * 5)],
      travail: ["A","B","B","C","B"][Math.floor(Math.random() * 5)],
      redouble: Math.random() > 0.9, nmc: Math.floor(Math.random() * 12) + 1,
    });
  }
  students.sort((a, b) => (b.moy_ann || 0) - (a.moy_ann || 0));
  students.forEach((s, i) => (s.rang = i + 1));
  return students.sort((a, b) => a.last_name.localeCompare(b.last_name));
}

export default function PVAnnuelPage() {
  const { lang } = useLang();
  const [schoolId, setSchoolId] = useState<number | null>(getUserSchoolId);
  const [selectedAcademicYearId, setSelectedAcademicYearId] = useState<number | null>(getSelectedYearId);
  const [currentAcademicYear, setCurrentAcademicYear] = useState<{id: number; name: string} | null>(null);

  const t = {
    fr: {
      title: "Procès-Verbal Annuel", subtitle: "PV d'évaluation annuelle (3 trimestres)",
      listTitle: "PROCES VERBAL ANNUEL", session: "Session : Délib Annuel Après Rattrapage",
      status: "Statut : Inscrits; Exclus", configTitle: "Configuration",
      section: "Section", cycle: "Cycle", level: "Niveau", class: "Classe",
      allSections: "Toutes", allCycles: "Tous", allLevels: "Tous", allClasses: "Toutes",
      academicYear: "Année académique", search: "Rechercher...",
      name: "Noms et prénoms", neLe: "Né(e) le", sexe: "Sexe", redouble: "Redoubl.",
      travail: "TRAVAIL", trim1: "Trim 1", trim2: "Trim 2", trim3: "Trim 3", moyAnn: "Moy.Ann", rang: "Rang",
      conduite: "CONDUITE", anj: "ANJ", jex: "JEx", decision: "Décision",
      exportPDF: "PDF", exportExcel: "Excel", exportExcelData: "Excel Data",
      noData: "Aucune donnée", loading: "Chargement...", noYearSelected: "Aucune année scolaire sélectionnée",
      yearRequired: "Veuillez sélectionner une année scolaire dans le header",
      classLabel: "Classe", totalStudents: "Total élèves", headerTitle: "En-tête de l'établissement",
      headerDesc: "Personnalisez l'en-tête qui apparaît sur chaque PV", schoolName: "Nom de l'établissement",
      schoolNameEn: "Nom (EN)", region: "Région", department: "Département", delegation: "Délégation",
      subDivision: "Arrondissement / Sub-Division", address: "Adresse", poBox: "BP / P.O. Box",
      logo: "Logo", saveHeader: "Sauvegarder l'en-tête", selectClasses: "Sélectionner des classes",
    },
    en: {
      title: "Annual Report Card", subtitle: "Annual evaluation report (3 terms)",
      listTitle: "ANNUAL EVALUATION REPORT", session: "Session: Annual Delib After Catch-up",
      status: "Status: Enrolled; Excluded", configTitle: "Configuration",
      section: "Section", cycle: "Cycle", level: "Level", class: "Class",
      allSections: "All", allCycles: "All", allLevels: "All", allClasses: "All",
      academicYear: "Academic year", search: "Search...",
      name: "Names", neLe: "Born on", sexe: "Sex", redouble: "Repeat.",
      travail: "WORK", trim1: "Term 1", trim2: "Term 2", trim3: "Term 3", moyAnn: "Ann.Avg", rang: "Rank",
      conduite: "CONDUCT", anj: "Unj.Abs", jex: "Excl.Days", decision: "Decision",
      exportPDF: "PDF", exportExcel: "Excel", exportExcelData: "Excel Data",
      noData: "No data", loading: "Loading...", noYearSelected: "No academic year selected",
      yearRequired: "Please select an academic year in the header",
      classLabel: "Class", totalStudents: "Total students", headerTitle: "School Header",
      headerDesc: "Customize the header that appears on each report", schoolName: "School Name",
      schoolNameEn: "Name (EN)", region: "Region", department: "Department", delegation: "Delegation",
      subDivision: "Sub Division", address: "Address", poBox: "P.O. Box",
      logo: "Logo", saveHeader: "Save Header", selectClasses: "Select classes",
    },
  }[lang];

  const [sections, setSections] = useState<any[]>([]);
  const [cycles, setCycles] = useState<any[]>([]);
  const [levels, setLevels] = useState<any[]>([]);
  const [classes, setClasses] = useState<any[]>([]);
  const [years, setYears] = useState<any[]>([]);
  const [periods, setPeriods] = useState<any[]>([]);

  const [selectedSection, setSelectedSection] = useState<string>("");
  const [selectedCycle, setSelectedCycle] = useState<string>("");
  const [selectedLevel, setSelectedLevel] = useState<string>("");
  const [selectedClassIds, setSelectedClassIds] = useState<number[]>([]);
  const [showClassDropdown, setShowClassDropdown] = useState(false);

  const [students, setStudents] = useState<PVAnnuelStudent[]>([]);
  const [loading, setLoading] = useState(false);
  const [refLoading, setRefLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortMode, setSortMode] = useState<"alpha" | "merit">("alpha");
  const [debugInfo, setDebugInfo] = useState<string>("");
  const [rawDebug, setRawDebug] = useState<any>(null);

  const [showHeaderConfig, setShowHeaderConfig] = useState(false);
  const [schoolHeader, setSchoolHeader] = useState<SchoolHeader>({
    republic_fr: "RÉPUBLIQUE DU CAMEROUN", republic_en: "REPUBLIC OF CAMEROON",
    motto_fr: "Paix-Travail-Patrie", motto_en: "Peace-Work-Fatherland",
    region: "RÉGION DE L'OUEST", department: "DÉPARTEMENT DE LA MIFI",
    delegation: "DÉLÉGATION DÉPARTEMENTALE DES ENSEIGNEMENTS SECONDAIRES",
    sub_division: "ARRONDISSEMENT DE BAFOUSSAM II",
    school_name: "LYCÉE BILINGUE DE BALENG", school_name_en: "GBHS BALENG",
    address: "BP : 1041 Baleng", po_box: "P.O. Box : 1041 Baleng", logo_url: "",
  });

  const getFilterParams = useCallback(() => {
    const params: Record<string, any> = {};
    if (schoolId) params.school_id = schoolId;
    if (selectedAcademicYearId) params.academic_year_id = selectedAcademicYearId;
    return params;
  }, [schoolId, selectedAcademicYearId]);

  useEffect(() => {
    const loadConfig = async () => {
      if (!schoolId || !selectedAcademicYearId) return;
      setRefLoading(true);
      try {
        const filterParams = getFilterParams();
        const [yearRes, secRes, cycRes, levRes, perRes] = await Promise.all([
          apiService.get(`/schools/${schoolId}/academic-years`).catch(() => ({ data: [] })),
          apiService.get("/sections", { params: filterParams }).catch(() => ({ data: [] })),
          apiService.get("/cycles", { params: filterParams }).catch(() => ({ data: [] })),
          apiService.get("/levels", { params: filterParams }).catch(() => ({ data: [] })),
          apiService.get("/grades/periods", { params: filterParams }).catch(() => ({ data: [] })),
        ]);
        setYears(extractData(yearRes));
        setSections(extractData(secRes));
        setCycles(extractData(cycRes));
        setLevels(extractData(levRes));
        setPeriods(extractData(perRes));
      } catch (e) {
        console.error("Config load error:", e);
      } finally {
        setRefLoading(false);
      }
    };
    loadConfig();
  }, [schoolId, selectedAcademicYearId, getFilterParams]);

  const loadClasses = useCallback(async () => {
    if (!schoolId || !selectedAcademicYearId || !selectedLevel) {
      setClasses([]); setSelectedClassIds([]); return;
    }
    try {
      const params: Record<string, any> = { ...getFilterParams(), level_id: parseInt(selectedLevel) };
      const res = await apiService.get("/grades/classes", { params });
      let data = extractData(res);
      const levelIdNum = parseInt(selectedLevel);
      data = data.filter((c: any) => {
        const cid = c.level_id != null ? parseInt(c.level_id) : c.levelId != null ? parseInt(c.levelId) : null;
        return cid === levelIdNum;
      });
      setClasses(data);
      setSelectedClassIds((prev) => prev.filter((id) => data.find((c: any) => c.id === id)));
    } catch {
      setClasses([]); setSelectedClassIds([]);
    }
  }, [schoolId, selectedAcademicYearId, selectedLevel, getFilterParams]);

  useEffect(() => { loadClasses(); }, [loadClasses]);

  const loadStudents = useCallback(async () => {
    if (!schoolId || !selectedAcademicYearId || selectedClassIds.length === 0) {
      setStudents([]); return;
    }
    setLoading(true);
    setDebugInfo("");
    setRawDebug(null);
    try {
      // Build mapping: evaluation_period_id → term number (1,2,3)
      const periodTermMap = new Map<number, number>();
      for (const p of periods) {
        const term = p.order_index ?? p.term ?? p.code?.replace(/\D/g, '') ?? 0;
        if (p.id && term) periodTermMap.set(Number(p.id), Number(term));
      }

      const allStudents: PVAnnuelStudent[] = [];
      let totalClassStudents = 0;
      let totalGrades = 0;
      let totalDisc = 0;

      for (const classId of selectedClassIds) {
        const clsName = classes.find((c: any) => c.id === classId)?.name || "";
        const [classStudents, allGrades, discMap] = await Promise.all([
          loadClassStudents(classId, getFilterParams()),
          loadGrades({ class_id: classId }),
          loadDisciplineForClass(classId, getFilterParams()),
        ]);
        totalClassStudents += classStudents.length;
        totalGrades += allGrades.length;
        totalDisc += discMap.size;

        // Split grades by term using evaluation_period_id
        const gradesT1 = allGrades.filter((g: any) => periodTermMap.get(getNum(g, "evaluation_period_id")) === 1);
        const gradesT2 = allGrades.filter((g: any) => periodTermMap.get(getNum(g, "evaluation_period_id")) === 2);
        const gradesT3 = allGrades.filter((g: any) => periodTermMap.get(getNum(g, "evaluation_period_id")) === 3);

        const t1Map = aggregateGradesByStudent(gradesT1);
        const t2Map = aggregateGradesByStudent(gradesT2);
        const t3Map = aggregateGradesByStudent(gradesT3);

        for (const cs of classStudents) {
          const sid = String(getValue(cs, "id"));
          const stu = normalizeFromClassStudent(
            cs, t1Map.get(sid), t2Map.get(sid), t3Map.get(sid), discMap.get(sid)
          );
          stu.class_name = clsName;
          allStudents.push(stu);
        }
      }

      setRawDebug({ totalClassStudents, totalGrades, totalDisc, firstStudent: allStudents[0] || null });

      if (allStudents.length > 0) {
        // 1. Sort by merit to calculate rank (rank is FIXED)
        const byMerit = [...allStudents].sort((a, b) => (b.moy_ann || 0) - (a.moy_ann || 0));
        byMerit.forEach((s, i) => (s.rang = i + 1));
        // 2. Store in alphabetical order by default
        setStudents(allStudents.sort((a, b) => a.last_name.localeCompare(b.last_name)));
        setDebugInfo(`${allStudents.length} élèves | ${totalGrades} notes | ${totalDisc} discipline`);
      } else {
        setStudents(generateMockPVAnnuel(30));
        setDebugInfo("Aucune donnée réelle — mock utilisé");
      }
    } catch (e: any) {
      console.error("[PVAnn] Load error:", e);
      setDebugInfo(`Erreur: ${e.message || e}`);
      setStudents(generateMockPVAnnuel(30));
    } finally {
      setLoading(false);
    }
  }, [schoolId, selectedAcademicYearId, selectedClassIds, getFilterParams, classes, periods]);

  useEffect(() => {
    if (selectedClassIds.length > 0) loadStudents();
  }, [selectedClassIds, loadStudents]);

  const displayStudents = useMemo(() => {
    let result = [...students];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter((s) =>
        `${s.last_name} ${s.first_name}`.toLowerCase().includes(term) || s.matricule.toLowerCase().includes(term)
      );
    }
    if (sortMode === "merit") {
      result.sort((a, b) => (b.moy_ann || 0) - (a.moy_ann || 0));
    } else {
      result.sort((a, b) => a.last_name.localeCompare(b.last_name));
    }
    return result;
  }, [students, searchTerm, sortMode]);

  const selectedClassNames = classes
    .filter((c: any) => selectedClassIds.includes(c.id))
    .map((c: any) => c.name)
    .join(", ");
  const yearLabel = years.find((y: any) => y.id === selectedAcademicYearId)?.label || years.find((y: any) => y.id === selectedAcademicYearId)?.name || "2025-2026";

  const handleExportCSV = () => {
    const rows = [[t.name, t.neLe, t.sexe, t.redouble, t.trim1, t.trim2, t.trim3, t.moyAnn, t.rang, t.anj, t.jex, t.decision]];
    displayStudents.forEach((s) => {
      rows.push([
        `${s.last_name} ${s.first_name}`, s.birth_date, s.gender, s.redouble ? "Oui" : "Non",
        String(s.trim1), String(s.trim2), String(s.trim3), String(s.moy_ann), String(s.rang),
        String(s.anj), String(s.jex), s.decision,
      ]);
    });
    downloadCSV(`pv-annuel-${selectedClassNames.replace(/, /g, "_") || "all"}.csv`, rows);
    toast.success("Export CSV généré");
  };

  const handleExportExcelData = () => {
    const data = displayStudents.map((s) => ({
      [t.name]: `${s.last_name} ${s.first_name}`,
      [t.neLe]: s.birth_date, [t.sexe]: s.gender, [t.redouble]: s.redouble ? "Oui" : "Non",
      [t.trim1]: s.trim1, [t.trim2]: s.trim2, [t.trim3]: s.trim3,
      [t.moyAnn]: s.moy_ann, [t.rang]: s.rang,
      [t.anj]: s.anj, [t.jex]: s.jex, [t.decision]: s.decision,
    }));
    downloadExcelData(
      `pv-annuel-data-${selectedClassNames.replace(/, /g, "_") || "all"}.csv`,
      data, schoolHeader, `${t.listTitle} / ${yearLabel}`,
      `${t.classLabel}: ${selectedClassNames || "—"}`
    );
    toast.success("Excel Data exporté");
  };

  const handleSaveHeader = () => {
    localStorage.setItem("lybibal-school-header", JSON.stringify(schoolHeader));
    toast.success(lang === "fr" ? "En-tête sauvegardé" : "Header saved");
  };

  const handlePrintPDF = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) { toast.error("Autorisez les popups"); return; }
    const today = new Date().toLocaleDateString(lang === "fr" ? "fr-FR" : "en-GB");
    let rowsHTML = "";
    displayStudents.forEach((s, i) => {
      const bg = s.decision === "Exclu" ? "#fee2e2" : i % 2 === 0 ? "#fff" : "#f9fafb";
      rowsHTML += `<tr style="background:${bg};">
        <td style="border:1px solid #333;padding:4px 6px;text-align:center;font-size:10px;">${i + 1}</td>
        <td style="border:1px solid #333;padding:4px 6px;font-size:10px;font-weight:600;">${s.last_name} ${s.first_name}</td>
        <td style="border:1px solid #333;padding:4px 6px;text-align:center;font-size:10px;">${s.birth_date}</td>
        <td style="border:1px solid #333;padding:4px 6px;text-align:center;font-size:10px;">${s.gender}</td>
        <td style="border:1px solid #333;padding:4px 6px;text-align:center;font-size:10px;">${s.redouble ? "Oui" : "Non"}</td>
        <td style="border:1px solid #333;padding:4px 6px;text-align:center;font-size:10px;color:#999;">${s.trim1 > 0 ? s.trim1 : "—"}</td>
        <td style="border:1px solid #333;padding:4px 6px;text-align:center;font-size:10px;color:#999;">${s.trim2 > 0 ? s.trim2 : "—"}</td>
        <td style="border:1px solid #333;padding:4px 6px;text-align:center;font-size:10px;color:#999;">${s.trim3 > 0 ? s.trim3 : "—"}</td>
        <td style="border:1px solid #333;padding:4px 6px;text-align:center;font-size:10px;font-weight:bold;">${s.moy_ann}</td>
        <td style="border:1px solid #333;padding:4px 6px;text-align:center;font-size:10px;">${s.rang}</td>
        <td style="border:1px solid #333;padding:4px 6px;text-align:center;font-size:10px;">${s.anj}</td>
        <td style="border:1px solid #333;padding:4px 6px;text-align:center;font-size:10px;">${s.jex}</td>
        <td style="border:1px solid #333;padding:4px 6px;text-align:center;font-size:10px;font-weight:bold;color:${s.decision === "Exclu" ? "#dc2626" : s.decision === "Rattrapage" ? "#d97706" : "#16a34a"}">${s.decision}</td>
      </tr>`;
    });

    const logoHtml = schoolHeader.logo_url
      ? `<img src="${schoolHeader.logo_url}" style="width:50px;height:50px;object-fit:contain;" />`
      : `<div style="width:45px;height:45px;background:#1e3a5f;border-radius:50%;display:flex;align-items:center;justify-content:center;"><span style="color:#fff;font-size:12px;font-weight:bold;">LB</span></div>`;

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${t.title}</title><style>
      @page{size:A4 portrait;margin:10mm}
      body{font-family:Arial,sans-serif;margin:0;padding:10px;font-size:10px}
      table{width:100%;border-collapse:collapse;font-size:9px}
      th{background:#1e3a5f;color:#fff;padding:5px 4px;border:1px solid #333;text-align:center;font-size:9px}
      td{border:1px solid #333;padding:4px 6px}
      .footer{margin-top:10px;text-align:center;font-size:8px;color:#999;border-top:1px solid #eee;padding-top:5px}
      @media print{.no-print{display:none}}
    </style></head><body>
      <div style="text-align:center;border-bottom:2px solid #1e3a5f;padding-bottom:6px;margin-bottom:10px;">
        <div style="display:flex;justify-content:space-between;align-items:flex-start;">
          <div style="flex:1;text-align:left;">
            <p style="margin:0;font-size:10px;font-weight:bold;color:#1e3a5f;text-transform:uppercase;">${schoolHeader.republic_fr}</p>
            <p style="margin:1px 0;font-size:8px;color:#555;">${schoolHeader.motto_fr}</p>
            <p style="margin:2px 0 1px 0;font-size:8px;color:#333;">${schoolHeader.region}</p>
            <p style="margin:1px 0;font-size:8px;color:#333;">${schoolHeader.department}</p>
            <p style="margin:1px 0;font-size:8px;color:#333;">${schoolHeader.delegation}</p>
            <p style="margin:1px 0;font-size:8px;color:#333;">${schoolHeader.sub_division}</p>
          </div>
          <div style="width:55px;text-align:center;margin:0 8px;flex-shrink:0;">${logoHtml}</div>
          <div style="flex:1;text-align:right;">
            <p style="margin:0;font-size:10px;font-weight:bold;color:#1e3a5f;text-transform:uppercase;">${schoolHeader.republic_en}</p>
            <p style="margin:1px 0;font-size:8px;color:#555;">${schoolHeader.motto_en}</p>
            <p style="margin:2px 0 1px 0;font-size:8px;color:#333;">${schoolHeader.region.replace("RÉGION DE L'", "WEST ").replace("RÉGION", "REGION")}</p>
            <p style="margin:1px 0;font-size:8px;color:#333;">${schoolHeader.department.replace("DÉPARTEMENT DE LA ", "").replace("DÉPARTEMENT", "DIVISION")}</p>
            <p style="margin:1px 0;font-size:8px;color:#333;">${headerEnDelegation(schoolHeader.delegation)}</p>
            <p style="margin:1px 0;font-size:8px;color:#333;">${schoolHeader.sub_division.replace("ARRONDISSEMENT", "SUB DIVISION")}</p>
          </div>
        </div>
        <div style="margin-top:5px;">
          <p style="margin:0;font-size:13px;font-weight:bold;color:#1e3a5f;">${schoolHeader.school_name}</p>
          <p style="margin:1px 0;font-size:10px;font-weight:600;color:#1e3a5f;">${schoolHeader.school_name_en}</p>
          <p style="margin:1px 0;font-size:8px;color:#666;">${schoolHeader.address} | ${schoolHeader.po_box}</p>
        </div>
      </div>
      <h1 style="text-align:center;font-size:14px;font-weight:bold;color:#1e3a5f;margin:6px 0;text-transform:uppercase;letter-spacing:1px;">${t.listTitle} / ${yearLabel}</h1>
      <p style="text-align:center;font-size:11px;margin:2px 0;"><strong>${selectedClassNames || ""}</strong></p>
      <p style="text-align:center;font-size:10px;margin:2px 0;color:#555;">${t.session}</p>
      <p style="text-align:center;font-size:10px;margin:2px 0;color:#555;">${t.status}</p>
      <div style="display:flex;justify-content:space-between;margin-bottom:6px;font-size:10px;">
        <div><strong>${t.classLabel}:</strong> ${selectedClassNames || "—"}</div>
        <div><strong>${t.totalStudents}:</strong> ${displayStudents.length}</div>
      </div>
      <table>
        <thead>
          <tr>
            <th rowspan="2" style="text-align:center;width:30px;">N°</th>
            <th rowspan="2" style="text-align:left;">${t.name}</th>
            <th rowspan="2">${t.neLe}</th>
            <th rowspan="2">${t.sexe}</th>
            <th rowspan="2">${t.redouble}</th>
            <th colspan="5">${t.travail}</th>
            <th colspan="2">${t.conduite}</th>
            <th rowspan="2">${t.decision}</th>
          </tr>
          <tr>
            <th>${t.trim1}</th>
            <th>${t.trim2}</th>
            <th>${t.trim3}</th>
            <th>${t.moyAnn}</th>
            <th>${t.rang}</th>
            <th>${t.anj}</th>
            <th>${t.jex}</th>
          </tr>
        </thead>
        <tbody>${rowsHTML}</tbody>
      </table>
      <div class="footer">${schoolHeader.school_name} — ${t.title} — ${today}</div>
      <div class="no-print" style="text-align:center;margin-top:8px;"><button onclick="window.print()" style="padding:6px 18px;background:#1e3a5f;color:#fff;border:none;border-radius:4px;cursor:pointer;">Imprimer</button></div>
    </body></html>`;
    printWindow.document.write(html);
    printWindow.document.close();
  };

  useEffect(() => {
    const yearId = getSelectedYearId();
    const yearLabel = localStorage.getItem(SELECTED_YEAR_LABEL_KEY);
    if (yearId) {
      setSelectedAcademicYearId(yearId);
      if (yearLabel) setCurrentAcademicYear({ id: yearId, name: yearLabel });
    }
    try {
      const storedSchool = localStorage.getItem("lybibal-selected-school-id");
      if (storedSchool) setSchoolId(Number(storedSchool));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const handleYearChange = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail?.year_id) {
        setSelectedAcademicYearId(Number(detail.year_id));
        if (detail.label) setCurrentAcademicYear({ id: Number(detail.year_id), name: detail.label });
        setSelectedClassIds([]); setStudents([]);
      }
    };
    window.addEventListener(SELECTED_YEAR_EVENT, handleYearChange);
    return () => window.removeEventListener(SELECTED_YEAR_EVENT, handleYearChange);
  }, []);

  useEffect(() => {
    try {
      const saved = localStorage.getItem("lybibal-school-header");
      if (saved) setSchoolHeader(JSON.parse(saved));
    } catch { /* ignore */ }
  }, []);

  if (!selectedAcademicYearId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center max-w-md">
          <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">{t.noYearSelected}</h2>
          <p className="text-sm text-gray-500">{t.yearRequired}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Scale className="w-7 h-7 text-[#1e3a8a]" />
          {t.title}
        </h1>
        <p className="text-sm text-gray-500 mt-1">{t.subtitle}</p>
        {currentAcademicYear && (
          <div className="flex items-center gap-2 mt-2">
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded-full">{currentAcademicYear.name}</span>
          </div>
        )}
        {debugInfo && (
          <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800 font-mono">
            Debug: {debugInfo}
          </div>
        )}
        {rawDebug && (
          <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800 font-mono">
            <div>Élèves: {rawDebug.totalClassStudents} | Notes: {rawDebug.totalGrades} | Discipline: {rawDebug.totalDisc}</div>
            <div>Premier: {rawDebug.firstStudent ? `${rawDebug.firstStudent.last_name} ${rawDebug.firstStudent.first_name} (moy:${rawDebug.firstStudent.moy_ann})` : "aucun"}</div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-1 space-y-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <Settings className="w-4 h-4 text-[#1e3a8a]" />
              {t.configTitle}
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t.academicYear}</label>
                <select value={selectedAcademicYearId ?? ""} onChange={(e) => setSelectedAcademicYearId(e.target.value ? Number(e.target.value) : null)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]">
                  <option value="">{t.academicYear}</option>
                  {years.map((y: any) => <option key={y.id} value={String(y.id)}>{y.label || y.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t.section}</label>
                <select value={selectedSection} onChange={(e) => { setSelectedSection(e.target.value); setSelectedCycle(""); setSelectedLevel(""); setSelectedClassIds([]); setClasses([]); }} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]">
                  <option value="">{t.allSections}</option>
                  {sections.map((s: any) => <option key={s.id} value={String(s.id)}>{s.name || s.label_fr}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t.cycle}</label>
                <select value={selectedCycle} onChange={(e) => { setSelectedCycle(e.target.value); setSelectedLevel(""); setSelectedClassIds([]); setClasses([]); }} disabled={!selectedSection} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] disabled:bg-gray-100 disabled:cursor-not-allowed">
                  <option value="">{t.allCycles}</option>
                  {cycles.filter((c: any) => !selectedSection || String(c.section_id) === selectedSection).map((c: any) => <option key={c.id} value={String(c.id)}>{c.name || c.label_fr}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{t.level}</label>
                <select value={selectedLevel} onChange={(e) => { setSelectedLevel(e.target.value); setSelectedClassIds([]); setClasses([]); }} disabled={!selectedCycle} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] disabled:bg-gray-100 disabled:cursor-not-allowed">
                  <option value="">{t.allLevels}</option>
                  {levels.filter((l: any) => !selectedCycle || String(l.cycle_id) === selectedCycle).map((l: any) => <option key={l.id} value={String(l.id)}>{l.name || l.label_fr}</option>)}
                </select>
              </div>
              <div className="relative">
                <label className="block text-xs font-medium text-gray-600 mb-1">{t.class}</label>
                <button
                  onClick={() => setShowClassDropdown(!showClassDropdown)}
                  disabled={!selectedLevel || classes.length === 0}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-left focus:ring-2 focus:ring-[#1e3a8a] disabled:bg-gray-100 disabled:cursor-not-allowed flex items-center justify-between"
                >
                  <span className="truncate">{selectedClassIds.length === 0 ? t.selectClasses : `${selectedClassIds.length} classe(s)`}</span>
                  {showClassDropdown ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                </button>
                {showClassDropdown && classes.length > 0 && (
                  <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                    <div className="p-2 border-b border-gray-100">
                      <label className="flex items-center text-xs cursor-pointer hover:bg-gray-50 px-2 py-1 rounded">
                        <input type="checkbox" checked={selectedClassIds.length === classes.length && classes.length > 0} onChange={(e) => { if (e.target.checked) setSelectedClassIds(classes.map((c: any) => c.id)); else setSelectedClassIds([]); }} className="mr-2" />
                        <span className="font-medium">{lang === "fr" ? "Tout sélectionner" : "Select all"}</span>
                      </label>
                    </div>
                    {classes.map((c: any) => (
                      <label key={c.id} className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm">
                        <input type="checkbox" checked={selectedClassIds.includes(c.id)} onChange={(e) => { if (e.target.checked) setSelectedClassIds((prev) => [...prev, c.id]); else setSelectedClassIds((prev) => prev.filter((id) => id !== c.id)); }} className="mr-2" />
                        <span>{c.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <button onClick={() => setShowHeaderConfig(!showHeaderConfig)} className="flex items-center justify-between w-full text-sm font-semibold text-gray-900">
              <span className="flex items-center gap-2"><Building2 className="w-4 h-4 text-[#1e3a8a]" />{t.headerTitle}</span>
              {showHeaderConfig ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {showHeaderConfig && (
              <div className="mt-4 space-y-3">
                <p className="text-xs text-gray-500">{t.headerDesc}</p>
                <div><label className="block text-xs text-gray-600 mb-1">{t.schoolName}</label><input value={schoolHeader.school_name} onChange={(e) => setSchoolHeader((p) => ({ ...p, school_name: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                <div><label className="block text-xs text-gray-600 mb-1">{t.schoolNameEn}</label><input value={schoolHeader.school_name_en} onChange={(e) => setSchoolHeader((p) => ({ ...p, school_name_en: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                <div><label className="block text-xs text-gray-600 mb-1">{t.region}</label><input value={schoolHeader.region} onChange={(e) => setSchoolHeader((p) => ({ ...p, region: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                <div><label className="block text-xs text-gray-600 mb-1">{t.department}</label><input value={schoolHeader.department} onChange={(e) => setSchoolHeader((p) => ({ ...p, department: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                <div><label className="block text-xs text-gray-600 mb-1">{t.delegation}</label><input value={schoolHeader.delegation} onChange={(e) => setSchoolHeader((p) => ({ ...p, delegation: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                <div><label className="block text-xs text-gray-600 mb-1">{t.subDivision}</label><input value={schoolHeader.sub_division} onChange={(e) => setSchoolHeader((p) => ({ ...p, sub_division: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                <div><label className="block text-xs text-gray-600 mb-1">{t.address}</label><input value={schoolHeader.address} onChange={(e) => setSchoolHeader((p) => ({ ...p, address: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                <div><label className="block text-xs text-gray-600 mb-1">{t.poBox}</label><input value={schoolHeader.po_box} onChange={(e) => setSchoolHeader((p) => ({ ...p, po_box: e.target.value }))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" /></div>
                <div>
                  <label className="block text-xs text-gray-600 mb-1">{t.logo}</label>
                  <input type="file" accept="image/*" onChange={(e) => { const file = e.target.files?.[0]; if (file) { const reader = new FileReader(); reader.onloadend = () => setSchoolHeader((p) => ({ ...p, logo_url: reader.result as string })); reader.readAsDataURL(file); } }} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-xs file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                  {schoolHeader.logo_url && (
                    <div className="mt-2 flex items-center gap-2">
                      <img src={schoolHeader.logo_url} alt="Logo" className="w-12 h-12 object-contain border rounded" />
                      <button onClick={() => setSchoolHeader((p) => ({ ...p, logo_url: "" }))} className="text-xs text-red-600 hover:text-red-800 underline">{lang === "fr" ? "Supprimer" : "Remove"}</button>
                    </div>
                  )}
                </div>
                <button onClick={handleSaveHeader} className="w-full px-4 py-2 bg-[#1e3a8a] text-white rounded-lg text-sm font-medium hover:bg-[#152a5e] flex items-center justify-center gap-2"><Save className="w-4 h-4" />{t.saveHeader}</button>
              </div>
            )}
          </div>
        </div>

        <div className="xl:col-span-2 space-y-4">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input type="text" placeholder={t.search} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] w-full" />
            </div>
            <select
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value as "alpha" | "merit")}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a] bg-white"
              title={sortMode === "alpha" ? "Tri alphabétique" : "Tri par mérite"}
            >
              <option value="alpha">{lang === "fr" ? "A → Z" : "A → Z"}</option>
              <option value="merit">{lang === "fr" ? "Mérite" : "Merit"}</option>
            </select>
            <div className="h-6 w-px bg-gray-200 hidden sm:block" />
            <button onClick={handlePrintPDF} className="px-4 py-2 bg-red-50 text-red-700 rounded-lg text-sm font-medium hover:bg-red-100 flex items-center gap-2 transition-colors"><Printer className="w-4 h-4" />{t.exportPDF}</button>
            <button onClick={handleExportCSV} className="px-4 py-2 bg-emerald-50 text-emerald-700 rounded-lg text-sm font-medium hover:bg-emerald-100 flex items-center gap-2 transition-colors"><FileSpreadsheet className="w-4 h-4" />{t.exportExcel}</button>
            <button onClick={handleExportExcelData} className="px-4 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-100 flex items-center gap-2 transition-colors"><Download className="w-4 h-4" />{t.exportExcelData}</button>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#1e3a5f] text-white">
                  <tr>
                    <th rowSpan={2} className="px-2 py-2 text-center text-[10px] font-semibold w-8">N°</th>
                    <th rowSpan={2} className="px-2 py-2 text-left text-[10px] font-semibold">{t.name}</th>
                    <th rowSpan={2} className="px-2 py-2 text-center text-[10px] font-semibold">{t.neLe}</th>
                    <th rowSpan={2} className="px-2 py-2 text-center text-[10px] font-semibold">{t.sexe}</th>
                    <th rowSpan={2} className="px-2 py-2 text-center text-[10px] font-semibold">{t.redouble}</th>
                    <th colSpan={5} className="px-2 py-2 text-center text-[10px] font-semibold border-b border-white/20">{t.travail}</th>
                    <th colSpan={2} className="px-2 py-2 text-center text-[10px] font-semibold border-b border-white/20">{t.conduite}</th>
                    <th rowSpan={2} className="px-2 py-2 text-center text-[10px] font-semibold">{t.decision}</th>
                  </tr>
                  <tr>
                    <th className="px-2 py-1 text-center text-[9px] font-semibold">{t.trim1}</th>
                    <th className="px-2 py-1 text-center text-[9px] font-semibold">{t.trim2}</th>
                    <th className="px-2 py-1 text-center text-[9px] font-semibold">{t.trim3}</th>
                    <th className="px-2 py-1 text-center text-[9px] font-semibold">{t.moyAnn}</th>
                    <th className="px-2 py-1 text-center text-[9px] font-semibold">{t.rang}</th>
                    <th className="px-2 py-1 text-center text-[9px] font-semibold">{t.anj}</th>
                    <th className="px-2 py-1 text-center text-[9px] font-semibold">{t.jex}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading || refLoading ? (
                    <tr><td colSpan={13} className="px-4 py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400 mx-auto" /><p className="text-sm text-gray-500 mt-2">{t.loading}</p></td></tr>
                  ) : displayStudents.length === 0 ? (
                    <tr><td colSpan={13} className="px-4 py-12 text-center text-gray-500"><FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" /><p className="text-sm">{t.noData}</p><p className="text-xs text-gray-400 mt-1">{debugInfo}</p></td></tr>
                  ) : (
                    displayStudents.map((s, index) => (
                      <tr key={`${s.id}-${s.class_name}`} className="hover:bg-gray-50 transition-colors">
                        <td className="px-2 py-2 text-center text-gray-500 text-[10px] w-8">{index + 1}</td>
                        <td className="px-2 py-2 font-medium text-gray-900 text-[10px]">{s.last_name} {s.first_name}</td>
                        <td className="px-2 py-2 text-center text-gray-500 text-[10px]">{s.birth_date}</td>
                        <td className="px-2 py-2 text-center text-gray-500 text-[10px]">{s.gender}</td>
                        <td className="px-2 py-2 text-center text-[10px]">{s.redouble ? "Oui" : "Non"}</td>
                        <td className="px-2 py-2 text-center text-[10px] text-gray-400">{s.trim1 > 0 ? s.trim1 : "—"}</td>
                        <td className="px-2 py-2 text-center text-[10px] text-gray-400">{s.trim2 > 0 ? s.trim2 : "—"}</td>
                        <td className="px-2 py-2 text-center text-[10px] text-gray-400">{s.trim3 > 0 ? s.trim3 : "—"}</td>
                        <td className="px-2 py-2 text-center text-gray-900 text-[10px] font-bold">{s.moy_ann}</td>
                        <td className="px-2 py-2 text-center text-gray-500 text-[10px]">{s.rang}</td>
                        <td className="px-2 py-2 text-center text-gray-500 text-[10px]">{s.anj}</td>
                        <td className="px-2 py-2 text-center text-gray-500 text-[10px]">{s.jex}</td>
                        <td className="px-2 py-2 text-center text-[10px]">{s.decision ? <span className={`inline-flex px-1 py-0.5 rounded text-[9px] font-medium ${s.decision === "Admis" ? "bg-green-100 text-green-700" : s.decision === "Rattrapage" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>{s.decision}</span> : null}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {displayStudents.length > 0 && (
              <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
                <span className="text-xs text-gray-500">{displayStudents.length} {lang === "fr" ? "élève(s)" : "student(s)"}</span>
                <span className="text-xs text-gray-500">{t.classLabel}: {selectedClassNames || (lang === "fr" ? "Toutes" : "All")}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function headerEnDelegation(fr: string): string {
  const map: Record<string, string> = {
    "DÉLÉGATION DÉPARTEMENTALE DES ENSEIGNEMENTS SECONDAIRES": "DIVISIONAL DELEGATION FOR SECONDARY EDUCATION",
  };
  return map[fr] || fr;
}